import {
  createAsyncThunk,
  createSelector,
  createSlice,
  Draft,
  PayloadAction,
} from '@reduxjs/toolkit';
import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { debounce, groupBy, remove } from 'lodash';
import uniqBy from 'lodash/uniqBy';
import {
  CRATE_FILTER_TYPES,
  CrateFilter,
  CrateFilterRange,
  CrateFilterType,
} from '../../components/crates/CrateFilter/types';
import { config } from '../../config';
import ApiService from '../../services/Api.service';
import { CrateService } from '../../services/Crate.service';
import RequestService from '../../services/Request.service';
import { NotFoundError } from '../../types/Errors';
import { iCategory } from '../../types/ICategory';
import { iCrate } from '../../types/ICrate';
import { iTrack } from '../../types/ITrack';
import { generateTrackID } from '../../utils/generateTrackID';
import { readTrackIsrc } from '../../utils/readTrackIdentity';
import type { RootState } from '../types';
import { updateCrateMetric } from './crateMetrics.slice';

type CrateState = {
  selectedCrate: iCrate | null;
  createdFrom: string;
  locationFrom: string;
  draft: boolean;
  currentRequestId: string | null;
  error: unknown;
  isSortable: boolean;
  isEditable: boolean;
  previewMode: boolean;
  filter: {
    isFilterable: boolean;
    isFilterActive: boolean;
    filters: CrateFilter;
    matchFilter: 'all' | 'matched' | 'unmatched' | 'review';
  };
};

const initialState: CrateState = {
  selectedCrate: null,
  createdFrom: '',
  locationFrom: '',
  draft: false,
  currentRequestId: null,
  error: null,
  isSortable: false,
  isEditable: false,
  previewMode: false,
  filter: {
    isFilterable: false,
    isFilterActive: false,
    filters: {
      energy: { lower: 40, upper: 100 },
      valence: { lower: 40, upper: 100 },
      danceability: { lower: 60, upper: 100 },
    },
    matchFilter: 'all',
  },
};

/**
 * Retrieves crate from API via ID
 */
export const fetchCrateById = createAsyncThunk(
  'crates/fetchCratesById',
  async (crateId: string, { rejectWithValue, fulfillWithValue }) => {
    try {
      const url = `${config.API_BASE_URL}/crates/${crateId}`;
      const data: iCrate = await RequestService.fetch({ url });
      return fulfillWithValue(data);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : String(err));
    }
  },
);

/**
 * Uploads an image to the API and returns a url to access the image
 */
export const uploadCrateImage = createAsyncThunk(
  'crates/uploadCrateImage',
  async (crateImageFile: File, { rejectWithValue, fulfillWithValue }) => {
    try {
      const chAPI = ApiService.getApiClient();
      const response = await chAPI.crateControllerUploadCrateImage({
        file: crateImageFile,
      });
      return fulfillWithValue(response.imageUrl);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : String(error),
      );
    }
  },
);

/**
 * Debounced functions ensures we don't make too many API requests at once to save crate changes.
 * State changes will occur immediately in Redux, but the API requests will only be sent after there has been 3 seconds with no additional requests to save
 */
const debouncedSaveCrateChanges = debounce(
  async (requestId: string, thunkAPI: GetThunkAPI<{ state: RootState }>) =>
    thunkAPI.dispatch(saveCrateChangesToApi(requestId)),
  3000,
  {
    leading: true, // Leading ensures that we don't need to wait 3 seconds for the initial API request, it will be sent immediately
  },
);

/**
 * Persists the current crate state to the API.
 * Uses the debounced function above to only send requests every 3 seconds.
 */
export const saveCrateChanges = createAsyncThunk(
  'crates/saveCrateChanges',
  async (_: undefined, thunkAPI: GetThunkAPI<{ state: RootState }>) => {
    // Calls to the API are debounced
    debouncedSaveCrateChanges(thunkAPI.requestId, thunkAPI);
    // However, we update the current request ID without a debounce.
    // This ensures that we only set isSaving=false when the most recent request has been fulfilled
    thunkAPI.dispatch(setCurrentRequestId(thunkAPI.requestId));
  },
);

/**
 * Fire-and-forget metric increment via dedicated API endpoint.
 * Uses server-side atomic increment with Redis lock -- no client-side count values sent.
 */
export const incrementCrateMetric = createAsyncThunk(
  'crates/incrementCrateMetric',
  async (
    { crateId, metric }: { crateId: string; metric: 'view' | 'save' },
    thunkAPI,
  ) => {
    try {
      const api = ApiService.getApiClient();
      if (metric === 'view') {
        await api.crateControllerIncrementView({ id: crateId });
      } else {
        await api.crateControllerIncrementSave({ id: crateId });
      }
      const crateState = (thunkAPI.getState() as RootState).crate;
      const metricsState = (thunkAPI.getState() as RootState).crateMetrics;
      const currentCrate = crateState.selectedCrate;
      const confirmedCount =
        metric === 'view'
          ? (currentCrate?.viewCount ?? 0) + 1
          : Math.max(
              metricsState[crateId]?.saveCount ?? 0,
              currentCrate?.saveCount ?? 0,
            ) + 1;
      thunkAPI.dispatch(updateCrateMetric({ crateId, metric, confirmedCount }));
      return { crateId, metric };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error instanceof Error ? error.message : String(error),
      );
    }
  },
);

/**
 * Immediately persists the current crate state to the API (no debounce)
 */
export const updateTrackBPM = createAsyncThunk(
  'crates/updateTrackBPM',
  async (
    { trackId, bpmValue }: { trackId: string; bpmValue: number | undefined },
    thunkAPI: GetThunkAPI<{ state: RootState }>,
  ) => {
    const { dispatch, getState } = thunkAPI;
    dispatch(setTrackField({ trackId, field: 'BPM', value: bpmValue }));
    const { selectedCrate, isEditable } = getState().crate;
    if (!selectedCrate?._id || !isEditable) {
      return thunkAPI.fulfillWithValue({ success: false });
    }
    try {
      const updatedCrate = await CrateService.updateMyCrate(selectedCrate);
      return thunkAPI.fulfillWithValue({ success: true, updatedCrate });
    } catch (error) {
      dispatch(
        setTrackField({
          trackId,
          field: 'BPM',
          value: getState().crate.selectedCrate?.Tracks?.find(
            (t) => t.ID === trackId,
          )?.BPM,
        }),
      );
      return thunkAPI.rejectWithValue({
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },
);

export const updateTrackField = createAsyncThunk(
  'crate/updateTrackField',
  async (
    {
      trackId,
      field,
      value,
    }: {
      trackId: string;
      field: keyof iTrack;
      value: any;
    },
    thunkAPI: GetThunkAPI<{ state: RootState }>,
  ) => {
    const { dispatch, getState } = thunkAPI;

    const originalValue = getState().crate.selectedCrate?.Tracks?.find(
      (t) => t.ID === trackId,
    )?.[field];

    dispatch(setTrackField({ trackId, field, value }));

    const { selectedCrate, isEditable } = getState().crate;
    if (!selectedCrate?._id || !isEditable) {
      dispatch(
        setTrackField({
          trackId,
          field,
          value: originalValue,
        }),
      );
      return thunkAPI.fulfillWithValue({ success: false });
    }

    try {
      const updatedCrate = await CrateService.updateMyCrate(selectedCrate);
      return thunkAPI.fulfillWithValue({ success: true, updatedCrate });
    } catch (error) {
      dispatch(
        setTrackField({
          trackId,
          field,
          value: originalValue,
        }),
      );
      return thunkAPI.rejectWithValue({
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },
);

export const saveCrateChangesNow = createAsyncThunk(
  'crates/saveCrateChangesNow',
  async (_: undefined, thunkAPI: GetThunkAPI<{ state: RootState }>) => {
    const { selectedCrate, isEditable } = thunkAPI.getState().crate;

    // Require a valid id and edit permissions
    if (!selectedCrate?._id || !isEditable) {
      return thunkAPI.fulfillWithValue({ updatedCrate: undefined });
    }

    try {
      const updatedCrate = await CrateService.updateMyCrate(selectedCrate);
      return thunkAPI.fulfillWithValue({ updatedCrate });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },
);

/**
 * Persists the current crate state to the API. Without any of the current request ID and debounce logic
 */
const saveCrateChangesToApi = createAsyncThunk(
  'crates/saveCrateChangesToApi',
  async (
    originalRequestId: string, // We have to pass the original request ID since it originates from a different thunk and this one will have a different request ID
    thunkAPI: GetThunkAPI<{ state: RootState }>,
  ) => {
    const { selectedCrate, isEditable, locationFrom } =
      thunkAPI.getState().crate;
    if (!selectedCrate || !selectedCrate._id) {
      console.warn('No selected crate or _id, aborting save.');
      return;
    }

    // Require a valid id and edit permissions
    if (!selectedCrate?._id || !isEditable) {
      // No-op save: immediately resolve so UI doesn't hang on isSaving
      if (locationFrom != 'mixable-crates') {
        return thunkAPI.fulfillWithValue({
          originalRequestId,
          updatedCrate: undefined,
        });
      }
    }

    const crateWithLocationFrom: iCrate & { locationFrom: string } = {
      ...selectedCrate,
      locationFrom,
    };
    // Can only save user crates //TODO: This will need to change. Not only user crates anymore
    // if (selectedCrate?.Type !== 'User' || !selectedCrate?._id) {
    //   return;
    // }
    try {
      const updatedCrate = await CrateService.updateMyCrate(
        crateWithLocationFrom,
      );
      return thunkAPI.fulfillWithValue({ originalRequestId, updatedCrate });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        originalRequestId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },
);

export const CrateSlice = createSlice({
  name: 'crate',
  initialState,
  reducers: {
    setSelectedCrate: (state, action: PayloadAction<iCrate>) => {
      // Reset sortable and filterable flags
      state.isSortable = false;
      state.filter.isFilterable = false;

      state.filter.isFilterActive = false;
      state.filter.matchFilter = 'all';
      state.filter.filters = initialState.filter.filters;

      const isSameCrate =
        state.selectedCrate?._id &&
        state.selectedCrate._id === action.payload._id;
      const preservedViewCount = isSameCrate
        ? Math.max(
            state.selectedCrate?.viewCount ?? 0,
            action.payload.viewCount ?? 0,
          )
        : action.payload.viewCount;
      const preservedSaveCount = isSameCrate
        ? Math.max(
            state.selectedCrate?.saveCount ?? 0,
            action.payload.saveCount ?? 0,
          )
        : action.payload.saveCount;

      state.selectedCrate = {
        ...action.payload,
        viewCount: preservedViewCount,
        saveCount: preservedSaveCount,
      };
      if (action.payload.Tracks) {
        CrateSlice.caseReducers.setCrateTracks(state, {
          type: 'setCrateTracks',
          payload: action.payload.Tracks,
        });
      }
      state.isEditable = state.selectedCrate.editable || false;
    },

    setCrateEditable: (state, action: PayloadAction<boolean>) => {
      state.isEditable = action.payload;
    },
    setPreviewMode: (state, action: PayloadAction<boolean>) => {
      state.previewMode = action.payload;
    },
    setCrateFilterActive: (state, action: PayloadAction<boolean>) => {
      state.filter.isFilterActive = action.payload;
    },

    updateCrateFilterValue: (
      state,
      action: PayloadAction<{
        filter: CrateFilterType;
        value: CrateFilterRange;
      }>,
    ) => {
      const { filter, value } = action.payload;
      state.filter.filters[filter] = value;
    },

    setMatchFilter: (
      state,
      action: PayloadAction<'all' | 'matched' | 'unmatched' | 'review'>,
    ) => {
      state.filter.matchFilter = action.payload;
    },

    setCrateTracks: (state, { payload }: PayloadAction<iTrack[]>) => {
      // uniqBy ensures we never load duplicate tracks
      const tracks = uniqBy(
        payload.map((track) => {
          // Check if any tracks are sortable or filterable to set in state
          // Done inside of map to avoid having to iterate through tracks array multiple times
          applyTrackProperties(track, state);
          return {
            ...track,
            ID: generateTrackID(track),
            isrc: readTrackIsrc(track),
          };
        }),
        'ID',
      );
      if (state.selectedCrate) {
        state.selectedCrate.Tracks = tracks;
      } else {
        state.selectedCrate = {
          // TODO: Framework for saving draft later
          Tracks: tracks,
          Title: 'Draft Crate',
          editable: true,
          _id: 'draft',
        };
      }
    },
    setTrackField: (
      state,
      action: PayloadAction<{
        trackId: string;
        field: keyof iTrack;
        value: any;
      }>,
    ) => {
      if (!state.selectedCrate?.Tracks) return;

      // Find the track and update the specific field
      const track = state.selectedCrate.Tracks.find(
        (t) => t.ID === action.payload.trackId,
      );
      if (track) {
        track[action.payload.field] = action.payload.value;
      }
    },

    addTrackToCrate: (state, { payload: track }: PayloadAction<iTrack>) => {
      const { selectedCrate } = state;
      if (!selectedCrate) {
        return;
      }
      const trackId = generateTrackID(track);
      if (selectedCrate.Tracks) {
        // If track already exists in crate, return and don't add it
        if (selectedCrate.Tracks.find((track) => track.ID === trackId)) {
          return;
        }
        if (track.ID) {
          track.SpotifyID = track.ID;
        }
        track.ID = trackId;

        selectedCrate.Tracks.push(track);
      } else {
        selectedCrate.Tracks = [track];
      }

      // Check if newly added track changes crate properties
      applyTrackProperties(track, state);
    },

    removeTrackFromCrate: (
      { selectedCrate },
      { payload: trackId }: PayloadAction<string>,
    ) => {
      if (!selectedCrate?.Tracks) {
        return;
      }
      const removedTracks = remove(
        selectedCrate.Tracks,
        (track) =>
          track.ID === trackId ||
          track._id === trackId ||
          track.SpotifyID === trackId,
      );

      if (!removedTracks.length) {
        throw new NotFoundError(
          `Track: ${trackId} does not exist on Crate ${selectedCrate._id}`,
        );
      }
    },

    setSelectedCrateName: (state, action: PayloadAction<string>) => {
      if (state.selectedCrate) {
        state.selectedCrate.Title = action.payload;
      } else {
        state.selectedCrate = {
          _id: 'draft',
          Title: action.payload,
          editable: true,
        };
      }
    },

    setSelectedCrateDescription: (state, action: PayloadAction<string>) => {
      if (state.selectedCrate) {
        state.selectedCrate.Description = action.payload;
      } else {
        throw new ReferenceError('There is no current crate');
      }
    },

    setSelectedCrateVideoEmbed: (state, action: PayloadAction<string>) => {
      if (state.selectedCrate) {
        state.selectedCrate.videoEmbedCode = action.payload;
      } else {
        throw new ReferenceError('There is no current crate');
      }
    },

    setSelectedCrateViewCount: (state) => {
      if (state.selectedCrate) {
        state.selectedCrate.viewCount =
          (state.selectedCrate.viewCount ?? 0) + 1;
      } else {
        throw new ReferenceError('There is no current crate');
      }
    },

    setSelectedCrateSaveCount: (state) => {
      if (state.selectedCrate) {
        state.selectedCrate.saveCount =
          (state.selectedCrate.saveCount ?? 0) + 1;
      }
    },
    setSelectedCrateStatus: (
      state,
      action: PayloadAction<
        'Published' | 'Draft' | 'Archived' | 'Pending Review'
      >,
    ) => {
      if (state.selectedCrate) {
        state.selectedCrate.Status = action.payload;
      } else {
        throw new ReferenceError('There is no current crate');
      }
    },

    setSelectedCrateGroupId: (state, action: PayloadAction<number>) => {
      if (state.selectedCrate) {
        state.selectedCrate.groupId = action.payload;
        // If moving into Crate Hackers group (1), reflect server policy immediately
        if (action.payload === 1) {
          state.selectedCrate.Status = 'Draft';
          state.selectedCrate.Type = 'Standard';
        }
      } else {
        throw new ReferenceError('There is no current crate');
      }
    },
    setSelectedCrateStreamingLinks: (
      state,
      { payload: links }: PayloadAction<Partial<iCrate>>,
    ) => {
      if (state.selectedCrate) {
        state.selectedCrate = { ...state.selectedCrate, ...links };
      } else {
        throw new ReferenceError('There is no current crate');
      }
    },

    setSelectedCrateCategories: (
      state,
      { payload: categories }: PayloadAction<iCategory[]>,
    ) => {
      if (state.selectedCrate) {
        state.selectedCrate.Category = categories;
      }
    },

    setCreatedFrom: (state, action: PayloadAction<string>) => {
      state.createdFrom = action.payload;
    },

    setLocationFrom: (state, action: PayloadAction<string>) => {
      state.locationFrom = action.payload;
    },

    setCurrentRequestId: (state, action: PayloadAction<string | null>) => {
      state.currentRequestId = action.payload;
    },

    clearCrate: (state) => {
      state.selectedCrate = null;
      state.isEditable = false;
      state.createdFrom = '';
      state.locationFrom = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveCrateChangesToApi.fulfilled, (state, action) => {
      const payload = (action.payload || {}) as {
        originalRequestId?: string;
        updatedCrate?: any;
      };
      const originalRequestId = payload.originalRequestId;
      if (payload.updatedCrate) {
        // Preserve the current isEditable state before updating
        const wasEditable = state.isEditable;
        const currentViewCount = state.selectedCrate?.viewCount ?? 0;
        const currentSaveCount = state.selectedCrate?.saveCount ?? 0;
        state.selectedCrate = {
          ...payload.updatedCrate,
          viewCount: Math.max(
            currentViewCount,
            payload.updatedCrate.viewCount ?? 0,
          ),
          saveCount: Math.max(
            currentSaveCount,
            payload.updatedCrate.saveCount ?? 0,
          ),
        };
        // Maintain edit mode if it was already active
        state.isEditable =
          wasEditable || state.selectedCrate?.editable || false;
      }
      if (!originalRequestId) {
        // Fallback: no-op saves should still clear saving state
        state.currentRequestId = null;
        state.error = null;
        return;
      }
      if (state.currentRequestId === originalRequestId) {
        state.currentRequestId = null;
        state.error = null;
      }
    });
    builder.addCase(saveCrateChangesToApi.rejected, (state, action) => {
      const { requestId } = action.meta;
      if (state.currentRequestId === requestId) {
        state.currentRequestId = null;
        state.error = action.error;
      }
    });
    // Immediate save (no debounce)
    builder.addCase(saveCrateChangesNow.fulfilled, (state, action) => {
      const payload = (action.payload || {}) as { updatedCrate?: any };
      if (payload.updatedCrate) {
        // Preserve the current isEditable state before updating
        const wasEditable = state.isEditable;
        const currentViewCount = state.selectedCrate?.viewCount ?? 0;
        const currentSaveCount = state.selectedCrate?.saveCount ?? 0;
        state.selectedCrate = {
          ...payload.updatedCrate,
          viewCount: Math.max(
            currentViewCount,
            payload.updatedCrate.viewCount ?? 0,
          ),
          saveCount: Math.max(
            currentSaveCount,
            payload.updatedCrate.saveCount ?? 0,
          ),
        };
        // Maintain edit mode if it was already active
        state.isEditable =
          wasEditable || state.selectedCrate?.editable || false;
      }
      // Clear any pending saving state if present
      state.currentRequestId = null;
      state.error = null;
    });
    builder.addCase(saveCrateChangesNow.rejected, (state, action) => {
      state.currentRequestId = null;
      state.error = action.error;
    });
    builder.addCase(uploadCrateImage.fulfilled, (state, action) => {
      if (state.selectedCrate) {
        state.selectedCrate.Image = action.payload;
      }
    });

    builder.addCase(uploadCrateImage.rejected, (state, action) => {
      state.error = action.payload;
    });

    builder.addCase(incrementCrateMetric.fulfilled, (state, action) => {
      if (!state.selectedCrate) return;
      const { metric } = action.meta.arg;
      if (metric === 'view') {
        state.selectedCrate.viewCount =
          (state.selectedCrate.viewCount ?? 0) + 1;
      } else if (metric === 'save') {
        state.selectedCrate.saveCount =
          (state.selectedCrate.saveCount ?? 0) + 1;
      }
    });

    builder.addCase(updateTrackField.fulfilled, (state, action) => {
      const payload = (action.payload || {}) as {
        success: boolean;
        updatedCrate?: any;
      };
      if (payload.success && payload.updatedCrate) {
        const wasEditable = state.isEditable;
        const currentViewCount = state.selectedCrate?.viewCount ?? 0;
        const currentSaveCount = state.selectedCrate?.saveCount ?? 0;
        state.selectedCrate = {
          ...payload.updatedCrate,
          viewCount: Math.max(
            currentViewCount,
            payload.updatedCrate.viewCount ?? 0,
          ),
          saveCount: Math.max(
            currentSaveCount,
            payload.updatedCrate.saveCount ?? 0,
          ),
        };
        if (payload.updatedCrate.Tracks) {
          CrateSlice.caseReducers.setCrateTracks(state, {
            type: 'setCrateTracks',
            payload: payload.updatedCrate.Tracks,
          });
        }
        state.isEditable =
          wasEditable || state.selectedCrate?.editable || false;
      }
    });
  },
});

const isTrackSortable = (track: iTrack) => !!(track.BPM && track.Key_Camelot);
const isTrackFilterable = (track: iTrack) =>
  !!(track.energy || track.danceability || track.popularity || track.valence);

/**
 * Sets crate's filterability of sortability based on a track's properties within it
 */
const applyTrackProperties = (track: iTrack, state: Draft<CrateState>) => {
  // A crate is sortable or filterable as long as a single track is sortable or filterable
  state.filter.isFilterable =
    state.filter.isFilterable || isTrackFilterable(track);
  state.isSortable = state.isSortable || isTrackSortable(track);
};

export const {
  setSelectedCrate,
  setCrateEditable,
  setPreviewMode,
  setCreatedFrom,
  setLocationFrom,
  setTrackField,
  clearCrate,
  setSelectedCrateName,
  setSelectedCrateDescription,
  setSelectedCrateVideoEmbed,
  setSelectedCrateViewCount,
  setSelectedCrateSaveCount,
  setSelectedCrateGroupId,
  setSelectedCrateStreamingLinks,
  setSelectedCrateCategories,
  setCrateTracks,
  addTrackToCrate,
  removeTrackFromCrate,
  setCurrentRequestId,
  setCrateFilterActive,
  updateCrateFilterValue,
  setMatchFilter,
  setSelectedCrateStatus,
} = CrateSlice.actions;

export const selectSelectedCrate = ({ crate }: { crate: CrateState }) =>
  crate.selectedCrate;

export const selectSelectedCrateId = ({ crate }: { crate: CrateState }) =>
  crate.selectedCrate?._id;

export const selectCreatedFrom = ({ crate }: { crate: CrateState }) =>
  crate.createdFrom;

export const selectLocationFrom = ({ crate }: { crate: CrateState }) =>
  crate.locationFrom;

export const selectCrateName = ({ crate }: { crate: CrateState }) =>
  crate.selectedCrate?.Title;

// Base selector to get crate slice
const selectCrateSlice = (state: { crate: CrateState }) => state.crate;

export const selectCrateState = createSelector([selectCrateSlice], (crate) => ({
  selectedCrate: crate.selectedCrate,
  createdFrom: crate.createdFrom,
  draft: crate.draft,
  currentRequestId: crate.currentRequestId,
  error: crate.error,
  isSortable: crate.isSortable,
  previewMode: crate.previewMode,
  filter: crate.filter,
  // Expose raw permission for components that need to show curator-only UI
  actualIsEditable: crate.isEditable,
  // Computed: respects preview mode - use this for edit controls
  isEditable: crate.isEditable && !crate.previewMode,
}));

export const selectAllCrateTracks = ({ crate }: { crate: CrateState }) =>
  crate.selectedCrate?.Tracks;

// Memoized selector for filtered crate tracks
export const selectFilteredCrateTracks = createSelector(
  [
    selectCrateSlice,
    (state: RootState) => state.trackMatcherState.selectedMatches,
    (state: RootState) => state.trackMatcherState.trackMatches,
    (state: RootState) => state.trackMatcherState.showMatcher,
  ],
  (
    crate,
    selectedMatches,
    trackMatches,
    showMatcher,
  ): { visible: iTrack[]; hidden: iTrack[] } => {
    const rawTracks = crate.selectedCrate?.Tracks;
    const { filters, matchFilter, isFilterActive } = crate.filter;

    if (!rawTracks) return { visible: [], hidden: [] };

    const seenIds = new Set<string>();
    const seenCompositeKeys = new Set<string>();

    const allTracks = rawTracks.filter((track) => {
      const id =
        track.ID || track._id || track.SpotifyID || generateTrackID(track);
      const artist = (track.Artist || '').trim().toLowerCase();
      const title = (track.Title || '').trim().toLowerCase();
      const compositeKey = `${artist}|${title}`;

      if (id && seenIds.has(id)) return false;
      if (compositeKey && seenCompositeKeys.has(compositeKey)) return false;

      if (id) seenIds.add(id);
      if (compositeKey) seenCompositeKeys.add(compositeKey);
      return true;
    });

    const groupedTracks = groupBy(allTracks, (track) => {
      const trackId = track.ID || generateTrackID(track);

      let matchesAudioFilters = true;
      if (isFilterActive) {
        matchesAudioFilters = CRATE_FILTER_TYPES.every((filter) => {
          const filterValue = track[filter];
          if (typeof filterValue !== 'number') return true;
          return (
            filterValue >= filters[filter].lower &&
            filterValue <= filters[filter].upper
          );
        });
      }

      if (!matchesAudioFilters) return 'hidden';

      if (!showMatcher || matchFilter === 'all') return 'visible';

      const hasSelectedMatch = (selectedMatches[trackId]?.length ?? 0) > 0;
      const potentialMatches = trackMatches[trackId] || [];
      const maxScore = potentialMatches.reduce(
        (max, m) => Math.max(max, m.match_score ?? 0),
        0,
      );

      let matchesMatchFilter = false;
      if (matchFilter === 'matched') {
        matchesMatchFilter =
          hasSelectedMatch || (potentialMatches.length > 0 && maxScore >= 80);
      } else if (matchFilter === 'unmatched') {
        matchesMatchFilter = potentialMatches.length === 0 && !hasSelectedMatch;
      } else if (matchFilter === 'review') {
        matchesMatchFilter =
          !hasSelectedMatch && potentialMatches.length > 0 && maxScore < 80;
      }

      return matchesMatchFilter ? 'visible' : 'discarded';
    });

    return {
      visible: groupedTracks.visible ?? [],
      hidden: groupedTracks.hidden ?? [],
    };
  },
);

export const selectIsCrateSaving = (state: { crate: CrateState }) =>
  !!state.crate.currentRequestId; // the crate is saving if there is any request in progress

export default CrateSlice.reducer;
